import _                                                           from 'underscore';
import moment, { Moment }                                          from 'moment';
import React, { FC, ReactNode, useContext, useEffect, useReducer } from 'react';
import { Button, Divider, Dropdown, Header, List, SemanticICONS }  from 'semantic-ui-react';
import { Cell, Grid }                                              from "styled-css-grid";
import { Link, useHistory }                                        from 'react-router-dom';

import { RouterUtility }                                                              from '../../../../../util';
import { CorvalProject, CorvalProjectVersion, PlanningAreaParamsSection, Size, Uuid } from '../../../../../types';
import { useCurrentUser, useFeatureFlags, useProject }                                from '../../../../../hooks';
import { ProjectService, TimeService }                          from '../../../../../services';
import { StyledContainer }                                      from '../../../../containers';
import { ProjectDashboardContext }                              from './ProjectDashboardIndexPage';
import { ProjectListItemContainer }                             from './ProjectListItemContainer';
import { ProjectListItemDetails }                               from './ProjectListItemDetails';
import { ProjectListItemTimestampStatement }                    from './ProjectListItemTimestampStatement';
import { ProjectListItemCalendar }                              from './ProjectListItemCalendar';
import { ContinueInquiryMessage }                               from './ContinueInquiryMessage';
import { PastProjectMessage }                                   from './PastProjectMessage';
import { RolePolicyType }                                       from '../../../../../services/RoleAccessService';
import { FinePrintMessage }                                     from "@src/views/components/util/messaging/FinePrintMessage";


interface OperationDescriptionObject {
    label: string,
    callback: ( ...params: any ) => void,
    secondary?: boolean,
    icon?: SemanticICONS | ReactNode,
}

interface Props {
    project: CorvalProject,
    projectVersion: CorvalProjectVersion,
}

const defaultProps: Props = {
    project:        null,
    projectVersion: null
}

type DashboardProjectListItemOperations =
    'manageProject' |
    'openProject' |
    'continueInquiry' |
    'reviewAssumptions' |
    'viewMap' |
    'viewBudget' |
    'viewHiring' |
    'duplicateProject';

type DashboardProjectListItemContextType = {
    project: CorvalProject,
    operations: Record<DashboardProjectListItemOperations, OperationDescriptionObject>,
    projectStats: ProjectListItemCompletionState,
    updateProjectStat: React.Dispatch<( { type: ProjectListItemCompletionDispatchTypes, value: any } )>,
};

type ProjectListItemCompletionDispatchTypes =
    'discoveryComplete';

export interface ProjectListItemCompletionState {
    isDiscoveryComplete: boolean,
    isPastProject: boolean,
}

type ProjectListItemCompletionReducer = ( state: ProjectListItemCompletionState, action: { type: ProjectListItemCompletionDispatchTypes, value: any } ) => any;

export const DashboardProjectListItemContext = React.createContext<DashboardProjectListItemContextType>( null );

const ProjectListItem: FC<Props> = ( props ) => {

    let history                                  = useHistory(),
        { hasProjectRoleAccessTo }               = useCurrentUser(),
        { openProject, duplicateProject }        = useProject(),
        { getMaybeCurrentProjectVersionFromSet } = useProject( props.project.id ),
        { projectListRequestRef }                = useContext( ProjectDashboardContext ),
        featureFlags                             = useFeatureFlags();

    let { project } = props;

    // Permission Checks not in LD
    let canUpdateSettings            = hasProjectRoleAccessTo( project.id, [ RolePolicyType.PROJECT_MANAGE_ACCESS ] ),
        canViewProjectPlanning       = featureFlags.permissionProjectPlanningView,
        canViewResourcePlanning      = featureFlags.permissionProjectResourcePlanningView,
        canRenameOrDuplicateProjects = featureFlags.enableProjectRenamingOrDuplicationFeatures;

    const statsReducer: ProjectListItemCompletionReducer = ( state, action ): ProjectListItemCompletionState => {

        let newState      = state,
            isPastProject = ( s: ProjectListItemCompletionState ) => {
                return ( false );
            };

        switch( action.type ){
            case 'discoveryComplete':
                newState = { ...state, isDiscoveryComplete: action.value };
                break;
            default:
                throw new Error();
        }

        return ( {
            ...newState,
            isPastProject: isPastProject( newState )
        } );
    }

    let [ projectStats, updateProjectStat ] = useReducer<ProjectListItemCompletionReducer>( statsReducer, {
        isDiscoveryComplete: project.isDiscoveryComplete,
        isPastProject:       false,
    } );

    let operations: DashboardProjectListItemContextType['operations'] = {
        openProject:       {
            label:    'Open Project',
            callback: () => handleOpenProject( project.id )
        },
        continueInquiry:   {
            label:    'Continue Inquiry',
            callback: () => handleContinueDiscovery( project.id )
        },
        reviewAssumptions: {
            label:     'Review & Planning',
            callback:  () => handleReviewAssumptions( project.id ),
            secondary: true,
        },
        viewMap:           {
            label:     'View Map',
            callback:  ( focusDate?: Moment ) => handleViewMap( project.id, focusDate ),
            secondary: true,
        },
        viewHiring:        {
            label:     'Review Hiring',
            callback:  () => handleViewHiring( project.id ),
            secondary: true,
        },
        viewBudget:        {
            label:     'Manage Resources',
            callback:  () => handleViewBudget( project.id ),
            secondary: true,
        },
        manageProject:     {
            label:    'Settings',
            callback: () => handleManageProject( project.id ),
            icon:     'cog',
        },
        duplicateProject:  {
            label:    'Duplicate',
            callback: () => handleDuplicateProject( project.id, project.name ),
            icon:     'copy',
        },
    }

    // && projectStats.isDiscoveryComplete
    let operationsMenuItems: OperationDescriptionObject[] = _.compact( [
        ( canViewProjectPlanning && canUpdateSettings ) ? operations.manageProject : null,
    ] )

    let managementOperations: OperationDescriptionObject[] = _.compact( [
        ( canViewProjectPlanning ) ? operations.reviewAssumptions : null,
        operations.viewMap,
        canViewResourcePlanning ? operations.viewBudget : null,
        operations.openProject,
    ] )

    let continuanceOperations = [
        // operations.continueInquiry
    ]

    let gatewayOperations: OperationDescriptionObject[] = managementOperations; // projectStats.isDiscoveryComplete ? managementOperations : continuanceOperations;

    const handleOpenProject = ( projectId: Uuid ) => {

        openProject( projectId );
    };

    const handleDuplicateProject = ( projectId: Uuid, projectName: string ) => {

        duplicateProject( projectId, projectName, async ( data: CorvalProject ) => {

            // add the new id into the search
            projectListRequestRef.current.variables.where.id.in.push( data.id );

            // refetch project listing with new id included
            await projectListRequestRef.current.refetch();

        } );
    };


    const handleContinueDiscovery = ( projectId: Uuid ) => {

        history.push( RouterUtility.getRoute( 'PROJECT.SURVEYS.INQUIRY', { projectId } ) );
    };

    const handleManageProject = ( projectId: Uuid ) => {

        history.push( RouterUtility.getRoute( 'PROJECT.MANAGE.SETTINGS', { projectId } ) );
    };

    const handleReviewAssumptions = ( projectId: Uuid ) => {

        history.push( RouterUtility.getRoute( 'PROJECT.PLANNING', { projectId, section: PlanningAreaParamsSection.ASSUMPTIONS_SUMMARY } ) );
    };

    const handleViewMap = ( projectId: Uuid, focusDate: Moment ) => {

        history.push( RouterUtility.getRoute( 'PROJECT.MAP', { projectId } ), {
            focusDate: focusDate ? TimeService.asDisplayDate( focusDate ) : null,
        } );
    };

    const handleViewHiring = ( projectId: Uuid ) => {

        history.push( RouterUtility.getRoute( 'PROJECT.RESOURCE_PLANNING.BUDGET_HIRING_DATA', { projectId } ) );
    };

    const handleViewBudget = ( projectId: Uuid ) => {

        history.push( RouterUtility.getRoute( 'PROJECT.RESOURCE_PLANNING.BUDGET_BY_DASHBOARD_DATA', { projectId } ) );
    };

    useEffect( () => {

        // ...analyze
        if( projectStats.isPastProject ){

            console.log( { project: project.name, ...projectStats } )

        }

    }, [ projectStats ] )


    const currentProjectVersion = props.projectVersion ?? getMaybeCurrentProjectVersionFromSet( project.projectVersions );

    return (

        <DashboardProjectListItemContext.Provider value={ { project, operations, projectStats, updateProjectStat } }>

            {/* Container */ }
            <ProjectListItemContainer>

                <Grid columns={ `minmax(auto,40%) 1fr` } rowGap={ '10px' } columnGap={ `80px` }>

                    <Cell>

                        <Grid columns={ `auto auto 1fr` } columnGap={ `20px` }>

                            <Cell middle>

                                {/* Project Name */ }
                                <Header as={ 'h2' } className={ 'no-margin-bottom text-capitalize' }>

                                    <Link to={ RouterUtility.getRoute( 'PROJECT.PLANNING', { projectId: project.id } ) }>{ project.name }</Link>

                                </Header>

                            </Cell>
                            <Cell middle>

                                {/* Project Menu Operations */ }
                                <Dropdown inline icon={ { name: 'bars', color: 'purple', fitted: true, } } direction={ 'left' }>

                                    <Dropdown.Menu>

                                        { operationsMenuItems.map( operationItem => (

                                            <Dropdown.Item
                                                key={ operationItem.label }
                                                text={ operationItem.label }
                                                onClick={ operationItem.callback }
                                                icon={ operationItem?.icon ? {
                                                    name:  operationItem?.icon,
                                                    color: 'purple'
                                                } : null }
                                            />

                                        ) ) }

                                    </Dropdown.Menu>

                                </Dropdown>

                            </Cell>
                            { currentProjectVersion && (
                                <Cell width={ 3 }>

                                    { currentProjectVersion && (
                                        <Grid columns={ `repeat(3,auto)` } areas={ [ 'label name name', '. desc desc' ] } columnGap={ `5px` } style={ { display: 'inline-grid' } }>
                                            <Cell area={ 'label' }><strong className={ 'text--colored-dk-screen-50' }>Version:</strong></Cell>
                                            <Cell area={ 'name' } width={ 2 }><strong>{ currentProjectVersion.name }</strong></Cell>
                                            <Cell area={ 'desc' } className={ 'padding-sm-bottom' }>
                                                <FinePrintMessage>{ currentProjectVersion.description }</FinePrintMessage>
                                            </Cell>
                                        </Grid>
                                    ) }

                                </Cell>
                            ) }
                        </Grid>


                        {/* Project Timestamps */ }
                        <StyledContainer padding={ [ Size.XS, null, null, null ] }>

                            <ProjectListItemTimestampStatement/>

                        </StyledContainer>

                    </Cell>

                    {/* Project Gateway Operations */ }
                    <Cell className={ 'text-right' }>

                        <List horizontal className={ 'no-margin' }>
                            { gatewayOperations.map( ( gatewayOperation, index ) => (

                                <List.Item key={ index }>

                                    <Button primary
                                            size={ 'tiny' }
                                            basic={ gatewayOperation?.secondary ?? false }
                                            onClick={ gatewayOperation.callback }
                                            content={ gatewayOperation.label }/>

                                </List.Item>

                            ) ) }
                        </List>

                    </Cell>

                    {/* Divider */ }
                    <Cell width={ 2 }>

                        <Divider/>

                    </Cell>

                    {/* Project Details */ }
                    <Cell left={ 1 } width={ 2 }>

                        <ProjectListItemDetails/>

                    </Cell>

                    {/* Continue Inquiry */ }
                    {/* !projectStats.isDiscoveryComplete && (

                     <Cell top={ 2 } height={ 2 } left={ 2 } className={ 'position--relative text-center' }>

                     <ContinueInquiryMessage/>

                     </Cell>
                     ) */ }

                    {/* Old Project */ }
                    { projectStats.isPastProject && (

                        <Cell top={ 2 } height={ 2 } left={ 2 } className={ 'position--relative text-center' }>

                            <PastProjectMessage/>

                        </Cell>
                    ) }

                </Grid>

            </ProjectListItemContainer>

        </DashboardProjectListItemContext.Provider>
    );
};

ProjectListItem.defaultProps = defaultProps;

export { ProjectListItem }
